import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, Typography } from '@lyearn/ui';

import AddInstructorDialog from './AddInstructorDialog';
import SelectedInstructors from './SelectedInstructors';
import { InstructorErrorType } from './types';

type AddInstructorProps = {
  className?: string;
  instructorIds?: string[];
  error?: InstructorErrorType;
  onChange: (instructorIds: string[]) => void;
  disabled?: boolean;
};

function AddInstructor({
  className,
  instructorIds,
  error,
  onChange,
  disabled,
}: AddInstructorProps) {
  const { t } = useTranslation();
  const [showDialog, { toggle: toggleDialog }] = useToggle(false);

  const onDelete = useCallback(
    (id: string) => {
      const selectedInstructors = instructorIds?.filter((userId) => userId !== id);
      if (selectedInstructors) {
        return onChange(selectedInstructors);
      }
    },
    [instructorIds, onChange],
  );

  const isInstructorEmpty = Boolean(error?.errorMessage && !error?.invalidInstructorsIds);
  const isInstructorIntegrationError = Boolean(error && !isInstructorEmpty);

  return (
    <>
      <Box className={cn('flex flex-col', className)}>
        <SelectedInstructors
          disabled={disabled}
          error={isInstructorIntegrationError ? error : undefined}
          instructorIds={instructorIds}
          onDelete={onDelete}
          onReplace={toggleDialog}
        />
        {disabled ? null : (
          <AddBlock title={t('Events:Editor.AddInstructor')} onClick={toggleDialog} />
        )}
        {isInstructorEmpty ? (
          <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
            {error?.errorMessage}
          </Typography>
        ) : null}
      </Box>
      {showDialog ? (
        <AddInstructorDialog
          selectedUsers={instructorIds}
          onClose={toggleDialog}
          onSubmit={onChange}
        />
      ) : null}
    </>
  );
}

export default React.memo(AddInstructor);
